const escapeHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

const formatNumber = (value, digits = 0) =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value || '');
};

export function buildRepartoPrintHtml({ sucursal, fecha, chofer, unidad, guides, totals, route, observaciones }) {
  const stopsByGuide = new Map((route?.paradas || []).map((stop) => [Number(stop.guiaId), stop]));
  const rows = guides.map((guide, index) => {
    const stop = stopsByGuide.get(Number(guide.id));
    const eta = stop?.llegadaEstimada ? `${escapeHtml(stop.llegadaEstimada)}<br>` : '';
    const distance = stop?.distanciaMetros ? `${formatNumber(stop.distanciaMetros / 1000, 1)} km` : '';
    return `
      <tr>
        <td class="num">${index + 1}</td>
        <td><strong>${escapeHtml(guide.guideNumber)}</strong>${guide.remito ? `<div class="muted">Rto ${escapeHtml(guide.remito)}</div>` : ''}</td>
        <td><strong>${escapeHtml(guide.recipient)}</strong><div>${escapeHtml(guide.address || 'Sin domicilio')}</div><div class="muted">${escapeHtml(guide.locality)}</div></td>
        <td class="right">${formatNumber(guide.bultos, 0)}</td>
        <td class="right">${formatNumber(guide.kilos, 1)}</td>
        <td class="right">${guide.volumen ? formatNumber(guide.volumen, 2) : '-'}</td>
        <td class="right">${guide.cashOnDelivery ? formatCurrency(guide.cashOnDelivery) : '-'}</td>
        <td class="center small">${eta}${distance}</td>
        <td class="sign"></td>
      </tr>`;
  }).join('');

  const capacity = unidad?.capacidad_m3 ? ` · capacidad ${formatNumber(unidad.capacidad_m3, 2)} m³` : '';
  const routeSummary = route
    ? `Recorrido ${route.recorridoEstimado ? 'estimado ' : ''}${formatNumber((route.distanciaMetros || 0) / 1000, 1)} km${route.duracionSegundos ? ` · ${formatNumber(route.duracionSegundos / 60, 0)} min de manejo` : ''}`
    : 'Orden manual (sin recorrido calculado)';

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Hoja de ruta - ${escapeHtml(formatDate(fecha))}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 10.5px; margin: 0; }
  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 6px; }
  h1 { font-size: 18px; margin: 0 0 2px; letter-spacing: 0.02em; }
  .muted { color: #555; font-size: 9.5px; }
  .meta { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; margin: 8px 0; }
  .meta div { border: 1px solid #bbb; border-radius: 4px; padding: 4px 6px; }
  .meta b { display: block; font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.08em; color: #555; }
  table { width: 100%; border-collapse: collapse; }
  thead { display: table-header-group; }
  th { background: #eee; font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.06em; text-align: left; }
  th, td { border: 1px solid #999; padding: 4px 5px; vertical-align: top; }
  tr { page-break-inside: avoid; }
  .num { width: 22px; text-align: center; font-weight: 700; font-size: 12px; }
  .right { text-align: right; white-space: nowrap; }
  .center { text-align: center; }
  .small { font-size: 9px; white-space: nowrap; }
  .sign { width: 150px; }
  tfoot td { font-weight: 700; background: #f6f6f6; }
  .obs { margin-top: 8px; border: 1px solid #bbb; border-radius: 4px; padding: 6px; min-height: 34px; }
  .signatures { display: flex; gap: 40px; margin-top: 32px; }
  .signatures div { flex: 1; border-top: 1px solid #111; padding-top: 3px; text-align: center; font-size: 9.5px; }
</style>
</head>
<body>
  <header>
    <div>
      <h1>Hoja de ruta</h1>
      <div class="muted">Sucursal ${escapeHtml(sucursal)} · ${escapeHtml(routeSummary)}</div>
    </div>
    <div class="muted" style="text-align:right">Impreso ${escapeHtml(new Date().toLocaleString('es-AR'))}</div>
  </header>
  <section class="meta">
    <div><b>Fecha</b>${escapeHtml(formatDate(fecha))}</div>
    <div><b>Chofer</b>${escapeHtml(chofer || 'Sin asignar')}</div>
    <div><b>Unidad</b>${escapeHtml(unidad?.patente || 'Sin asignar')}${escapeHtml(unidad?.tipo ? ` · ${unidad.tipo}` : '')}${escapeHtml(capacity)}</div>
    <div><b>Carga</b>${formatNumber(totals.guias, 0)} guías · ${formatNumber(totals.bultos, 0)} bultos · ${formatNumber(totals.kilos, 1)} kg · ${formatNumber(totals.m3, 2)} m³</div>
  </section>
  <table>
    <thead>
      <tr><th>#</th><th>Guía</th><th>Destinatario / domicilio</th><th>Bultos</th><th>Kg</th><th>m³</th><th>Cobrar (CR)</th><th>Llegada / tramo</th><th>Recibió (firma y aclaración)</th></tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr><td colspan="3">Totales</td><td class="right">${formatNumber(totals.bultos, 0)}</td><td class="right">${formatNumber(totals.kilos, 1)}</td><td class="right">${formatNumber(totals.m3, 2)}</td><td class="right">${formatCurrency(totals.cr)}</td><td colspan="2"></td></tr>
    </tfoot>
  </table>
  <div class="obs"><b>Observaciones:</b> ${escapeHtml(observaciones || '')}</div>
  <div class="signatures"><div>Firma chofer</div><div>Firma responsable de sucursal</div><div>Control de regreso</div></div>
</body>
</html>`;
}

export function printRepartoSheet(data) {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(buildRepartoPrintHtml(data));
  doc.close();
  const cleanup = () => window.setTimeout(() => frame.remove(), 500);
  frame.contentWindow.addEventListener('afterprint', cleanup, { once: true });
  window.setTimeout(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
  }, 150);
}
